/**
 * Bloom's taxonomy (revised) as data.
 * @module taxonomy/blooms
 */

import type { Taxonomy } from "./types";

/** Six ranked levels, lowest first. A verb may sit at more than one. */
export const BLOOMS_TAXONOMY: Taxonomy = {
  id: "blooms",
  name: "Bloom's Taxonomy",
  shortName: "Bloom's",
  citation:
    "Anderson, L. W. and Krathwohl, D. R. (eds) (2001) A Taxonomy for Learning, Teaching, and Assessing: A Revision of Bloom's Taxonomy of Educational Objectives. New York: Longman.",
  levels: [
    {
      id: "remember",
      rank: 1,
      name: "Remember",
      description: "Recall facts, terms, and basic concepts",
      verbs: [
        { lemma: "define" },
        { lemma: "list" },
        { lemma: "identify" },
        { lemma: "name" },
        { lemma: "recall" },
        { lemma: "recognise", aliases: ["recognize"] },
        { lemma: "state" },
        { lemma: "label" },
        { lemma: "match" },
        { lemma: "memorise", aliases: ["memorize"] },
      ],
    },
    {
      id: "understand",
      rank: 2,
      name: "Understand",
      description: "Explain ideas or concepts in own words",
      verbs: [
        { lemma: "describe" },
        { lemma: "explain" },
        { lemma: "interpret" },
        { lemma: "summarise", aliases: ["summarize"] },
        { lemma: "classify" },
        { lemma: "compare" },
        { lemma: "discuss" },
        { lemma: "distinguish" },
        { lemma: "paraphrase" },
        { lemma: "illustrate" },
      ],
    },
    {
      id: "apply",
      rank: 3,
      name: "Apply",
      description: "Use knowledge in new or practical situations",
      verbs: [
        { lemma: "apply" },
        { lemma: "demonstrate" },
        { lemma: "implement" },
        { lemma: "solve" },
        { lemma: "use" },
        { lemma: "execute" },
        { lemma: "carry out" },
        { lemma: "calculate" },
        { lemma: "operate" },
        { lemma: "practise" },
      ],
    },
    {
      id: "analyse",
      rank: 4,
      name: "Analyse",
      description: "Break information into parts and explore relationships",
      verbs: [
        { lemma: "analyse", aliases: ["analyze"] },
        { lemma: "differentiate" },
        { lemma: "examine" },
        { lemma: "compare" },
        { lemma: "contrast" },
        { lemma: "investigate" },
        { lemma: "categorise", aliases: ["categorize"] },
        { lemma: "deconstruct" },
        { lemma: "organise", aliases: ["organize"] },
      ],
    },
    {
      id: "evaluate",
      rank: 5,
      name: "Evaluate",
      description: "Justify a decision, appraise, or critique",
      verbs: [
        { lemma: "evaluate" },
        { lemma: "appraise" },
        { lemma: "argue" },
        { lemma: "critique" },
        { lemma: "defend" },
        { lemma: "judge" },
        { lemma: "justify" },
        { lemma: "assess" },
        { lemma: "recommend" },
        { lemma: "prioritise", aliases: ["prioritize"] },
      ],
    },
    {
      id: "create",
      rank: 6,
      name: "Create",
      description: "Produce new or original work, synthesise ideas",
      verbs: [
        { lemma: "create" },
        { lemma: "design" },
        { lemma: "develop" },
        { lemma: "formulate" },
        { lemma: "construct" },
        { lemma: "devise" },
        { lemma: "synthesise", aliases: ["synthesize"] },
        { lemma: "compose" },
        { lemma: "originate" },
        { lemma: "propose" },
      ],
    },
  ],
};
